import type { Timestamp } from '@firebase/firestore';

/**
 * Converts Firestore Timestamp or Date to a readable date string
 */
export function formatDate(
  date: Timestamp | Date | undefined,
  locale = 'de-DE'
): string {
  if (!date) return '';

  const dateObj = 'toDate' in date ? date.toDate() : date;

  return dateObj.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Converts Firestore Timestamp or Date to a relative time string
 */
export function formatRelativeTime(
  date: Timestamp | Date | undefined,
  locale = 'de-DE'
): string {
  if (!date) return '';

  const dateObj = 'toDate' in date ? date.toDate() : date;
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      if (diffMinutes < 2) return 'gerade eben';
      return `vor ${diffMinutes} Minuten`;
    }
    if (diffHours === 1) return 'vor einer Stunde';
    return `vor ${diffHours} Stunden`;
  }

  if (diffDays === 1) return 'gestern';
  if (diffDays < 7) return `vor ${diffDays} Tagen`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return weeks === 1 ? 'vor einer Woche' : `vor ${weeks} Wochen`;
  }

  return formatDate(date, locale);
}

/**
 * Format rating with one decimal place
 */
export function formatRating(rating: number): string {
  return rating.toFixed(1);
}

/**
 * Truncate text to a maximum length
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

/**
 * Format address from place data
 */
export function formatAddress(
  address: string,
  postalCode?: string,
  city?: string
): string {
  const parts = [address];
  if (postalCode || city) {
    parts.push([postalCode, city].filter(Boolean).join(' '));
  }
  return parts.join(', ');
}

/**
 * Get a simple location string from address
 */
export function getLocationFromAddress(address: string): string {
  // Extract city from address like "Marktplatz 5, Weilheim"
  const parts = address.split(',');
  if (parts.length >= 2) {
    return parts[parts.length - 1].trim();
  }
  return address;
}
